import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import {
  AuthProvider as OidcAuthProvider,
  useAuth as useOidcAuth,
} from 'react-oidc-context';
import { UserManager } from 'oidc-client-ts';

import { COLORS } from '@constants/colors';

import {
  createUserManager,
  onSigninCallback,
  setActiveUserManager,
} from '../helpers/configure';
import { resolveCognitoParams } from '../helpers/bootstrap';

/**
 * Full-screen spinner shown while the tenant Cognito pool resolves.
 *
 * Deliberately built from MUI primitives and a static colour rather than the
 * shared `LoadingProgress`: this renders above `MytaverseProvider`, and that
 * component's styles hook reads `useMytaverse()`, which would pull the whole
 * provider module graph into the bootstrap path.
 */
const AuthLoadingScreen = () => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100vh',
    }}
    data-testid="auth-bootstrap-loader"
  >
    <CircularProgress sx={{ color: COLORS.MYTAVERSE_BLUE }} size={40} />
  </Box>
);

/**
 * Holds children back until react-oidc-context has finished initialising.
 *
 * The library does **not** complete the post-redirect `?code=` exchange before
 * rendering children: it runs the exchange in an effect and renders children
 * unconditionally from the first commit, with `isLoading: true`. Mounting
 * `MytaverseProvider` straight away therefore let its mount-once auth effect
 * read the session while the exchange was still in flight, find none, and send
 * the participant back to the login screen — so signing in appeared to need two
 * attempts (MV-2088).
 *
 * @param props.children - App tree, mounted once the session has settled.
 */
const OidcSessionGate = ({ children }: { children: React.ReactNode }) => {
  const { isLoading } = useOidcAuth();

  if (isLoading) {
    return <AuthLoadingScreen />;
  }

  return <>{children}</>;
};

/**
 * Resolves this host's Cognito pool, then mounts the OIDC provider beneath it.
 *
 * The pool is per-tenant and resolved over HTTP, so it cannot be known at
 * import time. Rendering the spinner until it lands is what lets `index.tsx`
 * render immediately — the Amplify version had to configure a global before the
 * `App` module graph evaluated, which is why it used a dynamic import instead.
 *
 * `OidcSessionGate` then holds children back until react-oidc-context has
 * finished initialising, so the post-redirect `?code=` exchange is complete
 * before anything beneath reads the session (MV-2088).
 *
 * @param props.children - App tree, mounted once the OIDC client is ready.
 */
const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [userManager, setUserManager] = useState<UserManager | null>(null);

  useEffect(() => {
    let cancelled = false;
    let createdUserManager: UserManager | null = null;

    const bootstrap = async () => {
      // Never rejects — falls back to the `env.js` pool. Also records the
      // project this domain serves, which later consumers depend on.
      const params = await resolveCognitoParams();

      if (cancelled) {
        return;
      }

      createdUserManager = createUserManager(params);

      // Registered before the first render beneath this provider, so the axios
      // interceptor, the websocket URL builder and the assistant token
      // providers all find a live manager.
      setActiveUserManager(createdUserManager);
      setUserManager(createdUserManager);
    };

    bootstrap();

    return () => {
      cancelled = true;

      if (createdUserManager) {
        setActiveUserManager(null);
        createdUserManager.stopSilentRenew();
      }
    };
  }, []);

  if (!userManager) {
    return <AuthLoadingScreen />;
  }

  return (
    <OidcAuthProvider
      userManager={userManager}
      onSigninCallback={onSigninCallback}
    >
      <OidcSessionGate>{children}</OidcSessionGate>
    </OidcAuthProvider>
  );
};

export default AuthProvider;
